import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Concediu {
  id: string;
  angajat: string;
  functie: string;
  parohieId?: string;
  dataInceput: string;
  dataSfarsit: string;
  tipConcediu: 'odihna' | 'medical' | 'maternitate' | 'studii' | 'personal';
  motiv: string;
  status: 'in_asteptare' | 'aprobat' | 'respins';
  zile: number;
  dataCerere: string;
  aprobatDe?: string;
  observatii?: string;
}

const Concedii: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTip, setFilterTip] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConcediu, setSelectedConcediu] = useState<Concediu | null>(null);
  const [showOnlyMyRequests, setShowOnlyMyRequests] = useState(false);

  // Mock data pentru concedii
  const [concedii] = useState<Concediu[]>([
    {
      id: '1',
      angajat: 'Părintele Gheorghe',
      functie: 'Paroh',
      parohieId: 'parohia1',
      dataInceput: '2024-02-01',
      dataSfarsit: '2024-02-15',
      tipConcediu: 'odihna',
      motiv: 'Concediu de odihnă',
      status: 'aprobat',
      zile: 15,
      dataCerere: '2024-01-15',
      aprobatDe: 'Episcopul Ioan',
      observatii: 'Aprobat cu observații'
    },
    {
      id: '2',
      angajat: 'Secretarul Ana',
      functie: 'Secretar',
      parohieId: 'parohia1',
      dataInceput: '2024-02-20',
      dataSfarsit: '2024-02-25',
      tipConcediu: 'medical',
      motiv: 'Concediu medical',
      status: 'in_asteptare',
      zile: 6,
      dataCerere: '2024-01-20'
    },
    {
      id: '3',
      angajat: 'Contabilul Maria',
      functie: 'Contabil',
      parohieId: 'parohia2',
      dataInceput: '2024-03-01',
      dataSfarsit: '2024-03-10',
      tipConcediu: 'studii',
      motiv: 'Curs de specializare',
      status: 'aprobat',
      zile: 10,
      dataCerere: '2024-01-25',
      aprobatDe: 'Vicarul Mihai'
    },
    {
      id: '4',
      angajat: 'Părintele Vasile',
      functie: 'Paroh',
      parohieId: 'parohia3',
      dataInceput: '2024-02-10',
      dataSfarsit: '2024-02-12',
      tipConcediu: 'personal',
      motiv: 'Eveniment familial',
      status: 'respins',
      zile: 3,
      dataCerere: '2024-01-30',
      observatii: 'Perioadă nefavorabilă'
    }
  ]);

  const tipuriConcediu = [
    { value: 'odihna', label: 'Concediu de odihnă' },
    { value: 'medical', label: 'Concediu medical' },
    { value: 'maternitate', label: 'Concediu de maternitate' },
    { value: 'studii', label: 'Concediu pentru studii' },
    { value: 'personal', label: 'Concediu personal' }
  ];

  const statusOptions = ['in_asteptare', 'aprobat', 'respins'];

  const filteredConcedii = concedii.filter(concediu => {
    const matchesSearch = concediu.angajat.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         concediu.motiv.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || concediu.status === filterStatus;
    const matchesType = !filterTip || concediu.tipConcediu === filterTip;
    const matchesMyRequests = !showOnlyMyRequests || 
      (user?.role === 'paroh' && concediu.parohieId === user.parohieId) ||
      (user?.role === 'episcop' || user?.role === 'vicar');
    
    return matchesSearch && matchesStatus && matchesType && matchesMyRequests;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aprobat': return 'success';
      case 'in_asteptare': return 'warning';
      case 'respins': return 'error';
      default: return 'default';
    }
  };

  const getTipColor = (tip: string) => {
    switch (tip) {
      case 'odihna': return 'primary';
      case 'medical': return 'error';
      case 'maternitate': return 'secondary';
      case 'studii': return 'info';
      case 'personal': return 'default';
      default: return 'default';
    }
  };

  const handleViewConcediu = (concediu: Concediu) => {
    setSelectedConcediu(concediu);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedConcediu(null);
  };

  const handleApprove = (concediuId: string) => {
    // Implementare aprobare concediu
    console.log('Aprobare concediu:', concediuId);
  };

  const handleReject = (concediuId: string) => {
    // Implementare respingere concediu
    console.log('Respingere concediu:', concediuId);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  const totalConcedii = concedii.length;
  const concediiInAsteptare = concedii.filter(c => c.status === 'in_asteptare').length;
  const concediiAprobate = concedii.filter(c => c.status === 'aprobat').length;
  const concediiRespinse = concedii.filter(c => c.status === 'respins').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Concedii
        </Typography>
        {hasPermission('concedii.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare cerere concediu nou */}}
          >
            Cerere concediu
          </Button>
        )}
      </Box>

      {/* Statistici */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total concedii
                </Typography>
              </Box>
              <Typography variant="h5">
                {totalConcedii}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventIcon color="warning" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  În așteptare
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {concediiInAsteptare}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ApproveIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Aprobate
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {concediiAprobate}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <RejectIcon color="error" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Respinse
                </Typography>
              </Box>
              <Typography variant="h5" color="error.main">
                {concediiRespinse}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Toate concediile" />
          <Tab label="În așteptare" />
          <Tab label="Aprobate" />
          <Tab label="Respinse" />
        </Tabs>

        {/* Tab Toate concediile */}
        <TabPanel value={activeTab} index={0}>
          {/* Filtre */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Caută concedii..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {statusOptions.map(status => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tip concediu</InputLabel>
                  <Select
                    value={filterTip}
                    onChange={(e) => setFilterTip(e.target.value)}
                    label="Tip concediu"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {tipuriConcediu.map(tip => (
                      <MenuItem key={tip.value} value={tip.value}>
                        {tip.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('');
                    setFilterTip('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
            {(user?.role === 'paroh' || user?.role === 'episcop' || user?.role === 'vicar') && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyMyRequests}
                      onChange={(e) => setShowOnlyMyRequests(e.target.checked)}
                    />
                  }
                  label="Arată doar cererile mele"
                />
              </Box>
            )}
          </Paper>

          {/* Tabel concedii */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Angajat</TableCell>
                  <TableCell>Funcție</TableCell>
                  <TableCell>Perioada</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Zile</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data cerere</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConcedii.map((concediu) => (
                  <TableRow key={concediu.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {concediu.angajat}
                      </Box>
                    </TableCell>
                    <TableCell>{concediu.functie}</TableCell>
                    <TableCell>
                      {concediu.dataInceput} - {concediu.dataSfarsit}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tipuriConcediu.find(t => t.value === concediu.tipConcediu)?.label}
                        color={getTipColor(concediu.tipConcediu) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{concediu.zile}</TableCell>
                    <TableCell>
                      <Chip
                        label={concediu.status.replace('_', ' ')}
                        color={getStatusColor(concediu.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{concediu.dataCerere}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewConcediu(concediu)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('concedii.write') && (
                        <IconButton color="secondary">
                          <EditIcon />
                        </IconButton>
                      )}
                      {hasPermission('concedii.write') && concediu.status === 'in_asteptare' && (
                        <>
                          <IconButton
                            onClick={() => handleApprove(concediu.id)}
                            color="success"
                          >
                            <ApproveIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleReject(concediu.id)}
                            color="error"
                          >
                            <RejectIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab-uri pentru statusuri specifice */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Concedii în așteptare de aprobare
          </Typography>
          <List>
            {concedii.filter(c => c.status === 'in_asteptare').map((concediu) => (
              <React.Fragment key={concediu.id}>
                <ListItem>
                  <ListItemIcon>
                    <EventIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${concediu.angajat} - ${concediu.functie}`}
                    secondary={`${concediu.dataInceput} - ${concediu.dataSfarsit} (${concediu.zile} zile)`}
                  />
                  <Box>
                    <Chip
                      label={tipuriConcediu.find(t => t.value === concediu.tipConcediu)?.label}
                      color={getTipColor(concediu.tipConcediu) as any}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {hasPermission('concedii.write') && (
                      <>
                        <IconButton
                          onClick={() => handleApprove(concediu.id)}
                          color="success"
                          size="small"
                        >
                          <ApproveIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleReject(concediu.id)}
                          color="error"
                          size="small"
                        >
                          <RejectIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Concedii aprobate
          </Typography>
          <List>
            {concedii.filter(c => c.status === 'aprobat').map((concediu) => (
              <React.Fragment key={concediu.id}>
                <ListItem>
                  <ListItemIcon>
                    <ApproveIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${concediu.angajat} - ${concediu.functie}`}
                    secondary={`${concediu.dataInceput} - ${concediu.dataSfarsit} • Aprobat de: ${concediu.aprobatDe}`}
                  />
                  <Chip
                    label={tipuriConcediu.find(t => t.value === concediu.tipConcediu)?.label}
                    color={getTipColor(concediu.tipConcediu) as any}
                    size="small"
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Concedii respinse
          </Typography>
          <List>
            {concedii.filter(c => c.status === 'respins').map((concediu) => (
              <React.Fragment key={concediu.id}>
                <ListItem>
                  <ListItemIcon>
                    <RejectIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${concediu.angajat} - ${concediu.functie}`}
                    secondary={`${concediu.dataInceput} - ${concediu.dataSfarsit} • ${concediu.observatii}`}
                  />
                  <Chip
                    label={tipuriConcediu.find(t => t.value === concediu.tipConcediu)?.label}
                    color={getTipColor(concediu.tipConcediu) as any}
                    size="small"
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>
      </Paper>

      {/* Dialog pentru vizualizare concediu */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii concediu - {selectedConcediu?.angajat}
        </DialogTitle>
        <DialogContent>
          {selectedConcediu && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Angajat
                </Typography>
                <Typography variant="body1">
                  {selectedConcediu.angajat}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Funcție
                </Typography>
                <Typography variant="body1">
                  {selectedConcediu.functie}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Perioada
                </Typography>
                <Typography variant="body1">
                  {selectedConcediu.dataInceput} - {selectedConcediu.dataSfarsit}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Zile
                </Typography>
                <Typography variant="body1">
                  {selectedConcediu.zile}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Tip concediu
                </Typography>
                <Chip
                  label={tipuriConcediu.find(t => t.value === selectedConcediu.tipConcediu)?.label}
                  color={getTipColor(selectedConcediu.tipConcediu) as any}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedConcediu.status.replace('_', ' ')}
                  color={getStatusColor(selectedConcediu.status) as any}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Motiv
                </Typography>
                <Typography variant="body1">
                  {selectedConcediu.motiv}
                </Typography>
              </Grid>
              {selectedConcediu.aprobatDe && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Aprobat de
                  </Typography>
                  <Typography variant="body1">
                    {selectedConcediu.aprobatDe}
                  </Typography>
                </Grid>
              )}
              {selectedConcediu.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedConcediu.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          {hasPermission('concedii.write') && selectedConcediu?.status === 'in_asteptare' && (
            <>
              <Button
                onClick={() => handleApprove(selectedConcediu!.id)}
                color="success"
                startIcon={<ApproveIcon />}
              >
                Aprobă
              </Button>
              <Button
                onClick={() => handleReject(selectedConcediu!.id)}
                color="error"
                startIcon={<RejectIcon />}
              >
                Respinge
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Concedii;

